import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Chip,
  Button,
  Divider,
} from '@mui/material';
import {
  ViewList as ListIcon,
  GridView as GridIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DocumentListView from '../components/documents/DocumentListView';
import DocumentGridView from '../components/documents/DocumentGridView';
import DocumentFilterSidebar from '../components/documents/DocumentFilterSidebar';
import { documentService } from '../services/document.service';

type ViewMode = 'list' | 'grid';
type SortBy = 'date' | 'name' | 'size' | 'category';
type SortOrder = 'asc' | 'desc';

interface FilterCriteria {
  categories: string[];
  tags: string[];
  dateRange: { start: Date | null; end: Date | null };
  uploadedBy: string[];
  fileTypes: string[];
}

// APIレスポンスをドキュメントインターフェースに変換するユーティリティ関数
const transformDocuments = (results: any[]) => {
  return results.map((doc: any) => ({
    id: doc.id,
    title: doc.title || doc.fileName || '',
    fileName: doc.fileName || '',
    description: doc.description || '',
    summary: doc.summary || '',
    category: doc.category,
    tags: doc.tags || [],
    uploadedBy: doc.createdByName || doc.createdBy || doc.currentVersion?.uploadedBy || 'Unknown',
    uploadedAt: doc.uploadedAt || doc.currentVersion?.uploadedAt || doc.createdAt,
    fileType: doc.mimeType?.split('/').pop() || doc.currentVersion?.mimeType?.split('/').pop() || 'unknown',
    size: doc.fileSize || doc.currentVersion?.fileSize || 0,
    version: doc.currentVersion?.versionNumber || doc.version || 1,
    thumbnail: null,
    analysisHistory: doc.analysis_history || [], // 分析履歴
  }));
};

const PAGE_SIZE = 10;

const DocumentsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [sortBy, setSortBy] = useState<SortBy>('date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [documents, setDocuments] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState<FilterCriteria>({
    categories: [],
    tags: [],
    dateRange: { start: null, end: null },
    uploadedBy: [],
    fileTypes: [],
  });

  // 追加読み込み中の重複リクエスト防止用ref
  const isLoadingMoreRef = useRef(false);

  // 初回読み込みおよびフィルタ/ソート変更時にドキュメントを取得
  useEffect(() => {
    const loadInitialDocuments = async () => {
      setLoading(true);
      setDocuments([]);
      setCurrentPage(1);
      setHasMore(true);

      try {
        const response = await documentService.getDocuments({
          page: 1,
          pageSize: PAGE_SIZE,
          category: filters.categories.length > 0 ? filters.categories[0] : undefined,
          tags: filters.tags,
          sortBy: sortBy,
          sortOrder: sortOrder,
        });

        const transformedDocuments = transformDocuments(response.results);
        setDocuments(transformedDocuments);
        setCurrentPage(response.page);
        setTotalCount(response.total);
        setHasMore(response.page < response.totalPages);
      } catch (error) {
        console.error('Failed to load documents:', error);
        setDocuments([]);
        setHasMore(false);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    loadInitialDocuments();
  }, [searchQuery, filters, sortBy, sortOrder]);

  // 追加読み込みハンドラ（ページネーション）
  const handleLoadMore = useCallback(async () => {
    // 読み込み中または追加データなしの場合はスキップ
    if (loading || loadingMore || !hasMore || isLoadingMoreRef.current) return;

    isLoadingMoreRef.current = true;
    setLoadingMore(true);

    const nextPage = currentPage + 1;

    try {
      const response = await documentService.getDocuments({
        page: nextPage,
        pageSize: PAGE_SIZE,
        category: filters.categories.length > 0 ? filters.categories[0] : undefined,
        tags: filters.tags,
        sortBy: sortBy,
        sortOrder: sortOrder,
      });

      const transformedDocuments = transformDocuments(response.results);

      // 重複排除: 既存のドキュメントIDと照合して新規のみ追加
      setDocuments(prev => {
        const existingIds = new Set(prev.map(doc => doc.id));
        const newDocs = transformedDocuments.filter((doc: any) => !existingIds.has(doc.id));
        return [...prev, ...newDocs];
      });

      setCurrentPage(response.page);
      setTotalCount(response.total);
      setHasMore(response.page < response.totalPages);
    } catch (error) {
      console.error('Failed to load more documents:', error);
    } finally {
      setLoadingMore(false);
      isLoadingMoreRef.current = false;
    }
  }, [loading, loadingMore, hasMore, currentPage, filters, sortBy, sortOrder]);

  const handleViewModeChange = (_: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    // useEffectがsearchQueryの変更を検知して自動的にページ1から再取得する
  };

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortChange = (newSortBy: SortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
    handleSortClose();
  };

  const handleFilterChange = (newFilters: FilterCriteria) => {
    setFilters(newFilters);
    // useEffectがfiltersの変更を検知して自動的にページ1から再取得する
  };

  const handleDocumentClick = (documentId: string) => {
    navigate(`/documents/${documentId}`);
  };

  const handleDeleteDocument = useCallback(async (documentId: string) => {
    try {
      await documentService.deleteDocument(documentId);
      setDocuments((prev) => prev.filter((d) => d.id !== documentId));
      setTotalCount((prev) => Math.max(0, prev - 1));
    } catch {
      // Error handling could be improved with a snackbar
    }
  }, []);

  const handleUpdateTags = useCallback(async (documentId: string, tags: string[]) => {
    try {
      await documentService.updateDocumentTags(documentId, tags);
      setDocuments((prev) =>
        prev.map((d) => (d.id === documentId ? { ...d, tags } : d)),
      );
    } catch {
      // Error handling could be improved with a snackbar
    }
  }, []);

  const handleUploadClick = () => {
    navigate('/documents/upload');
  };

  const activeFiltersCount =
    filters.categories.length +
    filters.tags.length +
    filters.uploadedBy.length +
    filters.fileTypes.length +
    (filters.dateRange.start || filters.dateRange.end ? 1 : 0);

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      {/* Filter Sidebar */}
      <DocumentFilterSidebar
        open={filterOpen}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClose={() => setFilterOpen(false)}
      />

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4">{t('documents.title')}</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleUploadClick}
            disabled={false}
          >
            {t('documents.uploadDocument')}
          </Button>
        </Box>

        {/* Controls Bar */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Search */}
            <TextField
              size="small"
              placeholder={t('documents.searchPlaceholder')}
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{ flexGrow: 1, minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* Filter Toggle */}
            <Button
              variant={filterOpen ? 'contained' : 'outlined'}
              startIcon={<FilterIcon />}
              onClick={() => setFilterOpen(!filterOpen)}
              endIcon={activeFiltersCount > 0 ? (
                <Chip label={activeFiltersCount} size="small" color="primary" />
              ) : null}
            >
              {t('documents.filters')}
            </Button>

            {/* Sort Menu */}
            <Button
              variant="outlined"
              startIcon={<SortIcon />}
              onClick={handleSortClick}
            >
              {t('documents.sortBy')}: {t(`documents.sortOptions.${sortBy}`)} ({t(`documents.sortOrder.${sortOrder}`)})
            </Button>
            <Menu
              anchorEl={sortAnchorEl}
              open={Boolean(sortAnchorEl)}
              onClose={handleSortClose}
            >
              <MenuItem onClick={() => handleSortChange('date')}>
                {t('documents.sortOptions.date')} {sortBy === 'date' && (sortOrder === 'asc' ? '↑' : '↓')}
              </MenuItem>
              <MenuItem onClick={() => handleSortChange('name')}>
                {t('documents.sortOptions.name')} {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
              </MenuItem>
              <MenuItem onClick={() => handleSortChange('size')}>
                {t('documents.sortOptions.size')} {sortBy === 'size' && (sortOrder === 'asc' ? '↑' : '↓')}
              </MenuItem>
              <MenuItem onClick={() => handleSortChange('category')}>
                {t('documents.sortOptions.category')} {sortBy === 'category' && (sortOrder === 'asc' ? '↑' : '↓')}
              </MenuItem>
            </Menu>

            <Divider orientation="vertical" flexItem />

            {/* View Mode Toggle */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
            >
              <ToggleButton value="list" aria-label={t('documents.listView')}>
                <ListIcon />
              </ToggleButton>
              <ToggleButton value="grid" aria-label={t('documents.gridView')}>
                <GridIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Paper>

        {/* Document List/Grid */}
        {viewMode === 'list' ? (
          <DocumentListView
            documents={documents}
            loading={loading}
            loadingMore={loadingMore}
            onDocumentClick={handleDocumentClick}
            searchQuery={searchQuery}
            sortBy={sortBy}
            sortOrder={sortOrder}
            filters={filters}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            totalCount={totalCount}
            onDelete={handleDeleteDocument}
            onUpdateTags={handleUpdateTags}
          />
        ) : (
          <DocumentGridView
            documents={documents}
            loading={loading}
            onDocumentClick={handleDocumentClick}
          />
        )}

        {/* Pagination will be added here */}
      </Box>
    </Box>
  );
};

export default DocumentsPage;
